import { useEffect, useRef } from "react";
import Hls, { ErrorTypes, Events } from "hls.js";

interface IProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  autoPlay: boolean;
  src: string;
  useHlsEngine: boolean;
  onMounted?: () => void;
}

export const useHlsVideo = ({ videoRef, autoPlay, src, useHlsEngine, onMounted }: IProps) => {
  const hlsInstanceRef = useRef<Hls | null>(null);

  useEffect(() => {
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
    }

    if (!Hls.isSupported() || !useHlsEngine || videoRef.current === null) {
      return;
    }

    const hlsInstance = new Hls({
      enableWorker: true,
      maxBufferLength: 2,
      maxMaxBufferLength: 5,
    });

    hlsInstance.on(Events.MANIFEST_PARSED, () => {
      if (autoPlay) {
        videoRef.current
          ?.play()
          .catch(() =>
            console.warn("Unable to autoplay")
          );
      }
    });

    hlsInstance.on(Events.ERROR, function (event, data) {
      if (data.fatal) {
        switch (data.type) {
        case ErrorTypes.NETWORK_ERROR:
          hlsInstance.startLoad();
          break;
        case ErrorTypes.MEDIA_ERROR:
          hlsInstance.recoverMediaError();
          break;
        default:
          hlsInstance.destroy();
        }
      }
    });

    hlsInstance.loadSource(src);
    hlsInstance.attachMedia(videoRef.current);

    hlsInstanceRef.current = hlsInstance;

    return () => {
      hlsInstanceRef.current?.destroy();
    };
  }, [videoRef, src, autoPlay]);

  useEffect(() => {
    onMounted && onMounted();
  }, []);
};
